// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The bordered plan viewport: canvas surface, zoom/fit controls and the symbol
// legend. The plan graphic is passed in as `children`, so the frame owns the
// viewport (zoom, clipping, legend) and the drawing owns the geometry.

import React, { useState } from 'react';
import { Add, Subtract, Maximize } from '@carbon/icons-react';
import { MissingRegion } from '../common/MissingValue';
import { PLAN_GEOMETRY_UNPUBLISHED } from './missingData';

export interface LegendItem {
  key: string;
  label: string;
  /** Swatch style: a filled chip, a dashed rule, or a bracket glyph. */
  kind: 'chip' | 'rule' | 'bracket';
  /** Swatch body colour — the same token the plan symbol is filled with. */
  color?: string;
  /** Chip outline, matching the symbol's stroke on the plan. */
  border?: string;
}

export const PLAN_LEGEND: LegendItem[] = [
  {
    key: 'crah',
    label: 'CRAH (supply / return air)',
    kind: 'chip',
    color: 'var(--fp-crah-fill)',
    border: 'var(--fp-crah)',
  },
  { key: 'pdu', label: 'PDU', kind: 'chip', color: 'var(--fp-pdu-fill)', border: 'var(--fp-pdu)' },
  {
    key: 'ups',
    label: 'UPS / battery lineup',
    kind: 'chip',
    color: 'var(--fp-ups-fill)',
    border: 'var(--fp-ups)',
  },
  { key: 'supply', label: 'Supply airflow', kind: 'rule', color: 'var(--fp-airflow)' },
  { key: 'return', label: 'Hot-air return', kind: 'rule', color: 'var(--fp-return)' },
  { key: 'future', label: 'Future (F)', kind: 'bracket' },
];

function Swatch({ item }: { item: LegendItem }) {
  if (item.kind === 'bracket') {
    return <Maximize size={12} className="fp-legend-glyph" aria-hidden="true" />;
  }
  return (
    <span
      className={`fp-legend-swatch fp-legend-swatch-${item.kind}`}
      style={{ background: item.color, borderColor: item.border }}
      aria-hidden="true"
    />
  );
}

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
/** Zoom-to-fit is the plan drawn whole, which is the graphic's own viewBox. */
const ZOOM_FIT = 1;

function ZoomControls({
  zoom,
  onZoom,
  disabled,
}: {
  zoom: number;
  onZoom: (_next: number) => void;
  disabled: boolean;
}) {
  const clamp = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(z.toFixed(2))));
  return (
    <div className="fp-zoom" role="group" aria-label="Plan zoom">
      <button
        type="button"
        className="fp-zoom-btn"
        aria-label="Zoom in"
        disabled={disabled || zoom >= ZOOM_MAX}
        onClick={() => onZoom(clamp(zoom + ZOOM_STEP))}
      >
        <Add size={16} />
      </button>
      <button
        type="button"
        className="fp-zoom-btn"
        aria-label="Zoom out"
        disabled={disabled || zoom <= ZOOM_MIN}
        onClick={() => onZoom(clamp(zoom - ZOOM_STEP))}
      >
        <Subtract size={16} />
      </button>
      <button
        type="button"
        className="fp-zoom-btn"
        aria-label="Fit to view"
        disabled={disabled || zoom === ZOOM_FIT}
        onClick={() => onZoom(ZOOM_FIT)}
      >
        <Maximize size={16} />
      </button>
    </div>
  );
}

export default function FloorPlanFrame({
  children,
  loading = false,
}: {
  children?: React.ReactNode;
  loading?: boolean;
}) {
  const [zoom, setZoom] = useState(ZOOM_FIT);
  const hasPlan = Boolean(children);

  return (
    <>
      <div className="fp-canvas" data-testid="floor-plan-canvas">
        {hasPlan ? (
          // Scrollable so a zoomed plan can be panned rather than clipped.
          <div className="fp-canvas-scroll">
            <div className="fp-canvas-stage" style={{ width: `${zoom * 100}%` }}>
              {children}
            </div>
          </div>
        ) : loading ? (
          <p className="fp-canvas-placeholder">Loading plan…</p>
        ) : (
          // The frame, its controls and the legend below all stay. Only the
          // drawing is absent, and it says why in the drawing's own space.
          <div className="fp-canvas-placeholder">
            <MissingRegion
              title="Plan drawing not available"
              detail={`This site has no floor plan to draw: ${PLAN_GEOMETRY_UNPUBLISHED}. The legend below describes the symbols the plan uses once geometry is published.`}
            />
          </div>
        )}
        <ZoomControls zoom={zoom} onZoom={setZoom} disabled={!hasPlan} />
      </div>

      <ul className="fp-legend" aria-label="Plan legend">
        {PLAN_LEGEND.map(item => (
          <li key={item.key} className="fp-legend-item">
            <Swatch item={item} />
            {item.label}
          </li>
        ))}
      </ul>
    </>
  );
}
